import axios from "src/utils/axios";
import { DisplayState } from "src/utils/DisplayStates";
import { removeTimeZoneOffset } from "src/utils/formatTime";
import {
  addComplementsAndFlat,
  isTheSameProduct,
  isTheSameProductForDisplays,
  isTheSameProductMenu,
  isTheSameProductMenuForDisplays,
} from "src/utils/utils";

const { createSlice } = require("@reduxjs/toolkit");

const KDS_GROUP_BY_KEY = "kds:groupBy";
const readGroupBy = () => {
  try {
    const v = localStorage.getItem(KDS_GROUP_BY_KEY);
    return v === "pedido" ? "pedido" : "mesa";
  } catch {
    return "mesa";
  }
};

const initialState = {
  displays: [],
  display: null,
  orders: [],
  history: [],
  cart: [],
  isLoading: false,
  error: null,
  hasError: false,
  currentWorkingDisplay: null,

  // "mesa" (default) o "pedido" — controla cómo se agrupan las tarjetas en KDS.
  // Persistido en localStorage para que cada cocinero conserve su preferencia.
  groupBy: readGroupBy(),

  orderBy: "asc",
  orderProperty: "",
  filterName: "",
  selectedPage: 0,
  selectedPerPage: 10,
};

const slice = createSlice({
  name: "displays",
  initialState,
  reducers: {
    startLoading(state) {
      state.isLoading = true;
    },

    stopLoading(state) {
      state.isLoading = false;
    },

    // HAS ERROR
    hasError(state, action) {
      state.isLoading = false;
      state.hasError = true;
    },

    getDisplaysSuccess(state, action) {
      state.isLoading = false;
      state.hasError = false;
      state.displays = action.payload;
    },

    getDisplaySuccess(state, action) {
      state.isLoading = false;
      state.display = state.displays.find(
        (display) => display.id === Number(action.payload),
      );
    },

    addDisplaySuccess(state, action) {
      state.isLoading = false;
      state.hasError = false;
      // La lista puede haberse recargado (displayKitchen/Read) con la pantalla
      // ya incluida antes de llegar aquí: sin esto aparecía dos veces.
      const created = action.payload;
      const exists =
        created?.id != null && state.displays.some((d) => d?.id === created.id);
      state.displays = exists
        ? state.displays.map((d) => (d?.id === created.id ? created : d))
        : [...state.displays, created];
    },

    updateDisplaySuccess(state, action) {
      state.isLoading = false;
      state.hasError = false;
      state.displays = state.displays.map((display) =>
        display.id === action.payload.id ? action.payload : display,
      );
    },

    deleteDisplaySuccess(state, action) {
      state.isLoading = false;
      state.displays = state.displays.map((display) => {
        if (display.id === action.payload) {
          return { ...display, isActive: false };
        }
        return display;
      });
    },

    activateDisplaySuccess(state, action) {
      state.isLoading = false;
      state.displays = state.displays.map((display) => {
        if (display.id === action.payload) {
          return { ...display, isActive: true };
        }
        return display;
      });
    },

    getOrdersDisplaySuccess(state, action) {
      const { displayId, orders, history, cart } = action.payload;

      state.isLoading = false;
      state.currentWorkingDisplay = displayId;
      state.orders = orders;
      state.history = history;
      state.cart = cart;
    },

    updateAllProductsSuccess(state, action) {
      const { mesaId, newDisplayState, oldState, products } = action.payload;

      const mesaOrders = state.orders.filter((u) => u.idMesa == mesaId);

      const newStateIsAvailable = mesaOrders?.find(
        (u) => u.state === newDisplayState,
      );

      if (!newStateIsAvailable) {
        state.orders = [
          ...state.orders.map((order) => {
            if (order.idMesa == mesaId && order.state == oldState) {
              return {
                ...order,
                state: newDisplayState,
                inProgressDate:
                  newDisplayState === DisplayState.IN_PROGRESS
                    ? removeTimeZoneOffset(new Date().toISOString())
                    : order.inProgressDate,
                completedDate:
                  newDisplayState === DisplayState.DONE
                    ? removeTimeZoneOffset(new Date().toISOString())
                    : order.completedDate,
                products: order.products?.map((product) => ({
                  ...product,
                  displayState: newDisplayState,
                  inProgressDate:
                    newDisplayState === DisplayState.IN_PROGRESS
                      ? removeTimeZoneOffset(new Date().toISOString())
                      : product.inProgressDate,
                  completedDate:
                    newDisplayState === DisplayState.DONE
                      ? removeTimeZoneOffset(new Date().toISOString())
                      : product.completedDate,
                })),
              };
            }

            return order;
          }),
        ];
      } else {
        const oldStateProducts =
          state.orders.find(
            (order) => order.idMesa == mesaId && order.state == oldState,
          )?.products || [];

        state.orders = state.orders.map((order) => {
          if (order.idMesa == mesaId && order.state == oldState) {
            return {
              ...order,
              products: [],
            };
          }

          if (order.idMesa == mesaId && order.state == newDisplayState) {
            const newStateProducts = order.products || [];
            const newAddedProducts = [];

            oldStateProducts.map((product) => {
              const existingProduct = newStateProducts.find((p) =>
                isTheSameProduct(p, product),
              );

              if (!existingProduct) {
                newAddedProducts.push({
                  ...product,
                  displayState: newDisplayState,
                  inProgressDate:
                    newDisplayState === DisplayState.IN_PROGRESS
                      ? removeTimeZoneOffset(new Date().toISOString())
                      : product.inProgressDate,
                  completedDate:
                    newDisplayState === DisplayState.DONE
                      ? removeTimeZoneOffset(new Date().toISOString())
                      : product.completedDate,
                });
              } else {
                existingProduct.quantity += product.quantity;
              }

              return product;
            }); // To avoid lint error

            return {
              ...order,
              products: [...order.products, ...newAddedProducts],
            };
          }

          return order;
        });
      }

      state.orders = state.orders.filter((order) => order.products.length > 0);

      if (newDisplayState === DisplayState.PRINTED) {
        const finalProducts =
          state.orders.find(
            (order) => order.idMesa == mesaId && order.state == newDisplayState,
          ).products || [];

        state.cart = state.cart.filter((product) => {
          const isProductOnUpdatedState = finalProducts.some((item) =>
            isTheSameProduct(item, product),
          );

          return !isProductOnUpdatedState;
        });
      }
    },

    updateProductStatusSuccess(state, action) {
      const {
        mesaId,
        sessionId,
        product,
        newDisplayState,
        oldState,
        quantity,
        moveAll,
        complementId,
      } = action.payload;

      const mesaOrders = state.orders.filter((u) => u.idMesa == mesaId);

      const mesa = mesaOrders[0]?.mesa;
      const zona = mesaOrders[0]?.zona;
      const newStateIsAvailable = mesaOrders?.find(
        (u) => u.state === newDisplayState,
      );

      if (!newStateIsAvailable) {
        state.orders = [
          ...state.orders.map((order) => {
            if (order.idMesa == mesaId && order.state == oldState) {
              return {
                ...order,
                products: moveAll
                  ? order.products?.filter(
                      (item) =>
                        !isTheSameProductForDisplays(item, product) &&
                        item.unidadesEliminadas !== product.unidadesEliminadas,
                    )
                  : order.products
                      ?.map((item) => {
                        if (
                          item.isMenu &&
                          isTheSameProductMenuForDisplays(item, product)
                        ) {
                          if (complementId !== null) {
                            return {
                              ...item,
                              complementos: item.complementos
                                .map((c) => {
                                  if (c.id === complementId) {
                                    return {
                                      ...c,
                                      unidades: c.unidades - quantity,
                                    };
                                  }
                                  return c;
                                })
                                .filter((c) => c.unidades > 0),
                            };
                          }

                          return {
                            ...item,
                            quantity: item.quantity - quantity,
                          };
                        }

                        if (isTheSameProductForDisplays(item, product)) {
                          return {
                            ...item,
                            quantity: item.quantity - quantity,
                          };
                        }

                        return item;
                      })
                      .filter(
                        (item) =>
                          item.quantity > 0 &&
                          (!item.isMenu || item.complementos.length > 0),
                      ),
              };
            }

            return order;
          }),
          {
            creationDate: removeTimeZoneOffset(new Date().toISOString()),
            inProgressDate:
              newDisplayState === DisplayState.IN_PROGRESS
                ? removeTimeZoneOffset(new Date().toISOString())
                : product.inProgressDate,
            completedDate:
              newDisplayState === DisplayState.DONE
                ? removeTimeZoneOffset(new Date().toISOString())
                : product.completedDate,
            idMesa: mesaId,
            idSession: sessionId,
            state: newDisplayState,
            mesa,
            zona,
            products: [
              {
                ...product,
                displayState: newDisplayState,
                inProgressDate:
                  newDisplayState === DisplayState.IN_PROGRESS
                    ? removeTimeZoneOffset(new Date().toISOString())
                    : product.inProgressDate,
                completedDate:
                  newDisplayState === DisplayState.DONE
                    ? removeTimeZoneOffset(new Date().toISOString())
                    : product.completedDate,
                quantity: quantity,
              },
            ],
          },
        ];

        state.orders = state.orders.filter(
          (order) => order.products.length > 0,
        );

        return;
      }

      state.orders = state.orders.map((order) => {
        if (order.idMesa == mesaId && order.state == oldState) {
          return {
            ...order,
            products: moveAll
              ? order.products?.filter(
                  (item) => !isTheSameProductForDisplays(item, product),
                )
              : order.products
                  ?.map((item) => {
                    if (
                      item.isMenu &&
                      isTheSameProductMenuForDisplays(item, product)
                    ) {
                      if (complementId !== null) {
                        return {
                          ...item,
                          complementos: item.complementos
                            .map((c) => {
                              if (c.id === complementId) {
                                return {
                                  ...c,
                                  unidades: c.unidades - quantity,
                                };
                              }
                              return c;
                            })
                            .filter((c) => c.unidades > 0),
                        };
                      }

                      return {
                        ...item,
                        quantity: item.quantity - quantity,
                      };
                    }

                    if (isTheSameProductForDisplays(item, product)) {
                      return {
                        ...item,
                        quantity: item.quantity - quantity,
                      };
                    }

                    return item;
                  })
                  .filter(
                    (item) =>
                      item.quantity > 0 &&
                      (!item.isMenu || item.complementos.length > 0),
                  ),
          };
        }

        if (order.idMesa == mesaId && order.state == newDisplayState) {
          const productExists = order.products.find((item) =>
            item.isMenu
              ? isTheSameProductMenuForDisplays(item, product)
              : isTheSameProductForDisplays(item, product),
          );

          const allProducts = productExists
            ? order.products.map((item) => {
                if (
                  item.isMenu &&
                  isTheSameProductMenuForDisplays(item, product)
                ) {
                  return {
                    ...item,
                    quantity: item.quantity + quantity,
                    complementos: addComplementsAndFlat(
                      item.complementos,
                      product.complementos,
                    ),
                  };
                }

                if (isTheSameProductForDisplays(item, product)) {
                  return {
                    ...item,
                    quantity: item.quantity + quantity,
                  };
                }

                return item;
              })
            : [
                ...order.products,
                {
                  ...product,
                  displayState: newDisplayState,
                  inProgressDate:
                    newDisplayState === DisplayState.IN_PROGRESS
                      ? removeTimeZoneOffset(new Date().toISOString())
                      : product.inProgressDate,
                  completedDate:
                    newDisplayState === DisplayState.DONE
                      ? removeTimeZoneOffset(new Date().toISOString())
                      : product.completedDate,
                  quantity: quantity,
                },
              ];

          return {
            ...order,
            products: allProducts,
          };
        }

        return order;
      });

      state.orders = state.orders.filter((order) => order.products.length > 0);

      if (newDisplayState === DisplayState.PRINTED) {
        const finalProducts =
          state.orders.find(
            (order) => order.idMesa == mesaId && order.state == newDisplayState,
          ).products || [];

        state.cart = state.cart
          .map((product) => {
            const isProductOnUpdatedState = finalProducts.some((item) =>
              item.isMenu
                ? isTheSameProductMenuForDisplays(item, product)
                : isTheSameProductForDisplays(item, product),
            );

            if (!isProductOnUpdatedState) return product;

            return {
              ...product,
              quantity:
                product.quantity -
                finalProducts.find((item) =>
                  item.isMenu
                    ? isTheSameProductMenuForDisplays(item, product)
                    : isTheSameProductForDisplays(item, product),
                ).quantity,
            };
          })
          .filter(
            (item) =>
              item.quantity > 0 &&
              (!item.isMenu || item.complementos.length > 0),
          );
      }
    },

    // SORTING AND FILTERING
    setOrderBy(state, action) {
      state.orderBy = action.payload.orderBy;
      state.orderProperty = action.payload.orderProperty;
    },
    setFilterName(state, action) {
      state.filterName = action.payload;
    },
    setSelectedPage(state, action) {
      state.selectedPage = action.payload;
    },
    setSelectedPerPage(state, action) {
      state.selectedPerPage = action.payload;
    },

    cleanSelectedDisplay(state) {
      state.display = null;
    },

    setGroupBy(state, action) {
      const next = action.payload === "pedido" ? "pedido" : "mesa";
      state.groupBy = next;
      try {
        localStorage.setItem(KDS_GROUP_BY_KEY, next);
      } catch {}
    },

    areAllItemsInSameState(state, action) {
      const order = state.orders.find((o) => o.idMesa === action.payload);
      if (!order || order.products?.length === 0) return true;

      const firstState = order.products[0].displayState;
      return order.products?.every((item) => item.displayState === firstState);
    },

    moveOrderToCompleted(state, action) {
      state.orders = state.orders.map((order) => {
        if (order.idMesa === action.payload) {
          return {
            ...order,
            products: order.products?.map((item) => ({
              ...item,
              displayState: DisplayState.DONE, // Move all items to completed state
            })),
            state: DisplayState.DONE, // Update order state to completed
          };
        }
        return order;
      });
    },

    updateAllItemsStatus(state, action) {
      state.orders = state.orders.map((order) => {
        if (order.idMesa === action.payload.idMesa) {
          return {
            ...order,
            products: order.products?.map((item) => ({
              ...item,
              displayState: action.payload.status, // Update all items to the new status
            })),
          };
        }
        return order;
      });
    },

    updateItemStatus(state, action) {
      const { idMesa, itemId, status } = action.payload;
      state.orders = state.orders.map((order) => {
        if (order.idMesa === idMesa) {
          return {
            ...order,
            products: order.products?.map((item) =>
              item.id === itemId ? { ...item, status } : item,
            ),
          };
        }
        return order;
      });
    },
  },
});

export default slice.reducer;

export const {
  startLoading,
  stopLoading,
  hasError,
  getDisplaysSuccess,
  getDisplaySuccess,
  addDisplaySuccess,
  updateDisplaySuccess,
  deleteDisplaySuccess,
  activateDisplaySuccess,
  getOrdersDisplaySuccess,
  updateProductStatusSuccess,
  updateAllProductsSuccess,
  setOrderBy,
  setFilterName,
  setSelectedPage,
  setSelectedPerPage,
  cleanSelectedDisplay,
  areAllItemsInSameState,
  moveOrderToCompleted,
  updateAllItemsStatus,
  updateItemStatus,
  setGroupBy,
} = slice.actions;

//--------------------------------------------------------------------

export function getDisplays() {
  return async (dispatch) => {
    dispatch(startLoading());
    try {
      const response = await axios.get("displayKitchen/Read");
      const data = response.data;
      dispatch(getDisplaysSuccess(data));
    } catch (error) {
      dispatch(hasError(error));
    }
  };
}

export function getDisplay(id) {
  return async (dispatch) => {
    dispatch(startLoading());
    try {
      const response = await axios.get(`displayKitchen/Read/${id}`);
      const data = response.data;
      dispatch(getDisplaySuccess(data.id));
    } catch (error) {
      dispatch(hasError(error));
    }
  };
}

export function addDisplay(display) {
  return async (dispatch) => {
    dispatch(startLoading());
    try {
      const response = await axios.post("displayKitchen/Create", display);
      const data = response.data;
      dispatch(addDisplaySuccess(data));
    } catch (error) {
      dispatch(hasError(error));
    }
  };
}

export function updateDisplay(display) {
  return async (dispatch) => {
    dispatch(startLoading());
    try {
      const response = await axios.put(`displayKitchen/Update`, display);
      const data = response.data;
      dispatch(updateDisplaySuccess(data));
    } catch (error) {
      dispatch(hasError(error));
    }
  };
}

// 2026-09-16 · Settings de estilo/comportamiento del KDS. Endpoint
// dedicado que NO toca nombre ni categorías del display — solo el
// campo SettingsJson. Backend dispara SignalR `SyncDisplay` tras
// persistir para que los KDS conectados refetch al vuelo.
export function updateDisplaySettings(displayId, settings) {
  return async (dispatch) => {
    try {
      const response = await axios.put(
        `displayKitchen/${displayId}/settings`,
        settings,
      );
      dispatch(updateDisplaySuccess(response.data));
      return response.data;
    } catch (error) {
      dispatch(hasError(error));
      throw error;
    }
  };
}

export function deleteDisplay(id) {
  return async (dispatch) => {
    dispatch(startLoading());
    try {
      dispatch(deleteDisplaySuccess(id));
      await axios.delete(`displayKitchen/Delete/${id}`);
    } catch (error) {
      dispatch(hasError(error));
    }
  };
}

export function activateDisplay(id) {
  return async (dispatch) => {
    dispatch(startLoading());
    try {
      dispatch(activateDisplaySuccess(id));
      await axios.put(`displayKitchen/Activate/${id}`);
    } catch (error) {
      dispatch(hasError(error));
    }
  };
}

export function getOrders(id) {
  return async (dispatch) => {
    try {
      const response = await axios.get(`displayKitchen/GetOrders/${id}`);
      const data = response.data;
      dispatch(
        getOrdersDisplaySuccess({
          displayId: id,
          orders: data.items,
          history: data.history,
          cart: data.cart,
        }),
      );
    } catch (error) {
      dispatch(hasError(error));
    }
  };
}

// Construye UN único item por movimiento. El backend distingue:
//   - IdComplement = null  → producto entero atómico (padre + complementos
//     proporcionalmente para compuestos; solo padre para simples).
//   - IdComplement != null → solo ese complemento; el padre se resincroniza
//     en backend con regla MIN/rest.
//
// Antes el front desglosaba un menú en N items (uno por complemento) lo que
// causaba que mover "1 menú" se interpretara como "1 unidad de cada
// complemento" y aparecieran restos sueltos en la siguiente columna.
function buildKitchenOrderItem(product, quantity, complementId = null) {
  return {
    IdComplement: complementId ?? null,
    IdProdut: product.id,
    CounterProductId: product.counterProductId || [product.idNumPedido],
    Quantity: quantity,
  };
}

export function updateProductStatus({
  displayId,
  mesaId,
  sessionId,
  product,
  newDisplayState,
  oldState,
  quantity,
  moveAll,
  syncState = false,
  complementId = null,
}) {
  return async (dispatch) => {
    dispatch(startLoading());
    try {
      const updateRequest = {
        IdDisplay: displayId,
        IdMesa: mesaId,
        IdSession: sessionId,
        Orders: [
          {
            IdOrder: product.idPedido,
            Items: [buildKitchenOrderItem(product, quantity, complementId)],
          },
        ],
        State: newDisplayState,
        OldState: oldState,
      };

      await axios.put(`displayKitchen/UpdateOrderStatus`, updateRequest);
      await dispatch(getOrders(displayId));
    } catch (error) {
      console.log(error);
      dispatch(hasError(error));
    } finally {
      dispatch(stopLoading());
    }
  };
}

export function updateAllProductsStatus({
  displayId,
  mesaId,
  sessionId,
  products,
  newDisplayState,
  oldState,
  employeeId,
  syncState = false,
}) {
  return async (dispatch) => {
    dispatch(startLoading());
    try {
      // Cada producto = un item atómico. Para menús/compuestos el back mueve
      // padre + complementos proporcionalmente con un solo item.
      const groupedByOrder = products.reduce((acc, product) => {
        const idPedido = product.idPedido;
        if (!acc[idPedido]) acc[idPedido] = [];
        acc[idPedido].push(buildKitchenOrderItem(product, product.quantity));
        return acc;
      }, {});

      const orders = Object.entries(groupedByOrder).map(([idPedido, items]) => ({
        IdOrder: idPedido,
        Items: items,
      }));

      const updateRequest = {
        IdDisplay: displayId,
        IdMesa: mesaId,
        IdSession: sessionId,
        Orders: orders,
        State: newDisplayState,
        OldState: oldState,
        employeeId: employeeId,
      };

      await axios.put(`displayKitchen/UpdateOrderStatus`, updateRequest);
      await dispatch(getOrders(displayId));
    } catch (error) {
      console.log(error);
      dispatch(hasError(error));
    } finally {
      dispatch(stopLoading());
    }
  };
}

export function sendTicketToPrint({
  displayId,
  mesaId,
  sessionId,
  products,
  newDisplayState,
  complementId = null,
  oldState,
  employeeId,
  quantity = null,
  print = true,
  syncState = true,
}) {
  return async (dispatch) => {
    dispatch(startLoading());
    try {
      // Mismo formato unificado que updateProductStatus/updateAllProductsStatus.
      // El backend (PrintPedido) reconoce IdComplement=null como "producto
      // entero" y añade el padre + sus complementos al ticket.
      const groupedByOrder = products.reduce((acc, product) => {
        const idPedido = product.idPedido;
        if (!acc[idPedido]) acc[idPedido] = [];
        const qty = quantity != null ? quantity : product.quantity;
        acc[idPedido].push(buildKitchenOrderItem(product, qty, complementId));
        return acc;
      }, {});

      const orders = Object.entries(groupedByOrder).map(([idPedido, items]) => ({
        IdOrder: idPedido,
        Items: items,
      }));

      const updateRequest = {
        IdDisplay: displayId,
        IdMesa: mesaId,
        IdSession: sessionId,
        Orders: orders,
        State: newDisplayState,
        OldState: oldState,
        employeeId: employeeId,
      };

      if (print) {
        axios.post(`displayKitchen/sendTicketToPrint`, updateRequest);
      }

      await axios.put(`displayKitchen/UpdateOrderStatus`, updateRequest);
      await dispatch(getOrders(displayId));
    } catch (error) {
      dispatch(hasError(error));
    } finally {
      dispatch(stopLoading());
    }
  };
}
